/**
 * @fileoverview Central shared type definitions, interfaces, constants, and utility functions
 * for the MediaShare.link platform. This file serves as the single source of truth for all
 * client-facing contracts and is auto-copied to both the web package and the FluffyBunny mobile
 * app (as serverTypes.ts) during build, ensuring type alignment across Lambda backends, the
 * web frontend, and the mobile client.
 *
 * @module interface
 *
 * @exports CORS_HEADERS - Standard CORS headers applied to all API Gateway Lambda responses
 * @exports ClientDetailCode - Enum of structured status/error codes returned to clients
 * @exports LambdaOutput - Standard Lambda response shape (statusCode, body, headers)
 * @exports LambdaOutputErrorBody - Error body structure with error message and detail code
 * @exports createErrorResponse - Helper to build standardized error responses from status code and detail code
 * @exports INVERTED_TIMESTAMP_MAX - Sentinel used to invert epoch ms for DynamoDB sort keys (newest-first ordering)
 * @exports INVERTED_TIMESTAMP_THRESHOLD - Cutoff above which a numeric timestamp is treated as inverted
 * @exports buildInvertedTimestampPrefix - Build the 13-digit inverted-timestamp prefix used in sort-key IDs
 * @exports getCreatedAtFromInvertedId - Decode the original epoch ms from an ID prefixed with an inverted timestamp
 * @exports normalizePossiblyInvertedTimestamp - Convert a timestamp field back to normal epoch ms if it was stored inverted
 * @exports ModerationLambdaInput - Input interface for content moderation operations
 * @exports ModerationEntry - Single moderation result with labels, confidence scores, and review status
 * @exports ModerationLambdaOutputOK - Paginated moderation query response
 * @exports ConversationKeys - Encryption key record for end-to-end encrypted conversations (DynamoDB: conversation-keys)
 * @exports ConversationParticipant - Per-user conversation membership record (DynamoDB: conversation-participants)
 * @exports MessageRecord - Encrypted message record with media metadata and edit history
 * @exports ConversationsLambdaInput / ConversationsLambdaOutput - Conversation management Lambda I/O
 * @exports MessagesLambdaInput / MessagesLambdaOutput - Message CRUD Lambda I/O
 * @exports ConversationRecord - Summary record for conversation listing
 * @exports StudioJobType - Union type for AI Studio job categories (text-to-image, image-to-image, text-to-video, image-to-video)
 * @exports StudioModelId - Union type of available AI generation model identifiers
 * @exports StudioModelConfig - Model configuration with prompts, technical name, and availability
 * @exports STUDIO_MODELS - Complete registry of all AI Studio models and their configurations
 * @exports getStudioModel - Lookup a single model config by ID
 * @exports getAvailableModelsForJobType - Filter available models by job type
 * @exports WatermarkType - Watermark brand type ("mediashare" | "fluffybunny")
 * @exports StudioJobStatus / STUDIO_JOB_STATUS - Job lifecycle states and constants
 * @exports StudioJob - Full job record stored in DynamoDB studioJobs table
 * @exports CreateStudioJobInput - Input for creating a new studio job
 * @exports StudioLambdaInput / StudioLambdaOutputOK - Studio Lambda I/O with pagination
 * @exports RecentMediaFile / JobOutputMediaFile - Media gallery file descriptors
 * @exports SubscriptionType - Tier codes: F (Free), P (Pro), M (Max)
 * @exports SubscriptionInfo - Subscription metadata stored in Cognito custom attribute
 * @exports SubscriptionConstraints - Rate limits and feature flags per subscription tier
 * @exports SUBSCRIPTION_CONSTRAINTS - Lookup table of tier constraints (jobs/day, jobs/week, video/audio enabled)
 * @exports getSubscriptionConstraints - Resolve constraints for a given subscription type
 * @exports AgeVerificationStatus / AgeVerificationMetadata - Age verification state and Rekognition results
 * @exports AgeVerificationLambdaInput / AgeVerificationLambdaOutputBody - Age verification Lambda I/O
 * @exports TypedInterface / NotificationRecord - Polymorphic notification records (comment, friend, studio job)
 * @exports ChannelData - Channel configuration including permissions and status
 * @exports VideoMetadata / PostData / TempFile - Content upload and post creation types
 * @exports ContentLambdaInput / ContentLambdaOutputOK - Content Lambda I/O (upload URLs, savePost)
 * @exports SavePostInput / SavePostOutput / PublishPostsFromTempInput / PublishPostsFromTempOutput - Post publishing contracts
 * @exports DebugLambdaInput / DebugLambdaOutput - Debug API for S3 and DynamoDB inspection
 * @exports ChatBotMessage / ChatBotLambdaInput / ChatBotLambdaOutput - Chatbot messaging interfaces
 * @exports ChatSession / ChatBotMessageRecord - Chat session and message record types
 * @exports CONTENT_POLICY_MODEL_ID - Bedrock model ID used for content policy evaluation
 * @exports AGE_VERIFIED_SYSTEM_PROMPT / DEFAULT_SYSTEM_PROMPT - Prompt-checking content policy system prompts
 * @exports AGE_VERIFIED_IMAGE_SYSTEM_PROMPT / DEFAULT_IMAGE_SYSTEM_PROMPT - Image-checking content policy system prompts
 *
 * @description
 * Data flow: This file is not triggered by events. It is imported at build time by virtually
 * every Lambda function in the system and copied to client packages. Lambda handlers use
 * these types to parse API Gateway request bodies, validate operations, and construct
 * responses. The content policy prompts are sent to Amazon Bedrock (Claude Haiku) at
 * runtime for studio prompt and image moderation.
 *
 * Dependencies:
 * - No AWS SDK imports; pure TypeScript types and constants
 * - Consumed by Lambdas interacting with: API Gateway, DynamoDB (channels, posts, comments,
 *   friends, notifications, studioJobs, chatbot, conversation-keys, conversation-participants,
 *   imageAnalysis, videoAnalysis, audioAnalysis, blockedPosts, reports), S3 ({stage}-content-mediashare-link),
 *   Cognito (custom:custom:subscriptionInfo attribute), MediaConvert, Rekognition, Amazon Bedrock, SQS
 * - Auto-copied to: web package, FluffyBunny mobile app (serverTypes.ts)
 *
 * Error patterns: The createErrorResponse() utility standardizes all Lambda error responses
 * with an HTTP status code and a ClientDetailCode enum value. Each subsystem defines its own
 * detail code range (2xxx for general, 40xxx for age verification, 75xxx for studio, 76xxx
 * for debug, 78xxx for ops, 7001 for unexpected). No try/catch exists in this file; error
 * handling is delegated to the consuming Lambda handlers which wrap their logic in a single
 * top-level try/catch per the project's error handling policy.
 */
// Interfaces used by client
export const CORS_HEADERS = {
    "Access-Control-Allow-Origin": "*",
    "Access-Control-Allow-Credentials": true,
};
export var ClientDetailCode;
(function (ClientDetailCode) {
    ClientDetailCode[ClientDetailCode["SUCCESS"] = 1000] = "SUCCESS";
    ClientDetailCode[ClientDetailCode["MISSING_EVENT_BODY"] = 2001] = "MISSING_EVENT_BODY";
    ClientDetailCode[ClientDetailCode["INVALID_OPERATION"] = 2002] = "INVALID_OPERATION";
    ClientDetailCode[ClientDetailCode["MISSING_CHANNEL_OWNER_ID"] = 2003] = "MISSING_CHANNEL_OWNER_ID";
    ClientDetailCode[ClientDetailCode["MISSING_CHANNEL_ID"] = 2004] = "MISSING_CHANNEL_ID";
    ClientDetailCode[ClientDetailCode["MISSING_POST_ID"] = 2005] = "MISSING_POST_ID";
    ClientDetailCode[ClientDetailCode["MISSING_BEARER_TOKEN"] = 2006] = "MISSING_BEARER_TOKEN";
    ClientDetailCode[ClientDetailCode["MISSING_AUTH_HEADER"] = 2007] = "MISSING_AUTH_HEADER";
    ClientDetailCode[ClientDetailCode["INVALID_AUTH_HEADER"] = 2008] = "INVALID_AUTH_HEADER";
    ClientDetailCode[ClientDetailCode["NOT_ALLOWED"] = 2009] = "NOT_ALLOWED";
    ClientDetailCode[ClientDetailCode["INVALID_SEQUENCE"] = 2010] = "INVALID_SEQUENCE";
    ClientDetailCode[ClientDetailCode["MISSING_REVIEW_STATUS"] = 2011] = "MISSING_REVIEW_STATUS";
    // Age Verification Codes
    ClientDetailCode[ClientDetailCode["AGE_VERIFICATION_SUCCESS"] = 40000] = "AGE_VERIFICATION_SUCCESS";
    ClientDetailCode[ClientDetailCode["AGE_VERIFICATION_NOT_FOUND"] = 40001] = "AGE_VERIFICATION_NOT_FOUND";
    ClientDetailCode[ClientDetailCode["AGE_VERIFICATION_FAILED"] = 40002] = "AGE_VERIFICATION_FAILED";
    ClientDetailCode[ClientDetailCode["AGE_VERIFICATION_INVALID_IMAGE"] = 40003] = "AGE_VERIFICATION_INVALID_IMAGE";
    ClientDetailCode[ClientDetailCode["AGE_VERIFICATION_UNDER_AGE"] = 40004] = "AGE_VERIFICATION_UNDER_AGE";
    ClientDetailCode[ClientDetailCode["AGE_VERIFICATION_S3_ERROR"] = 40005] = "AGE_VERIFICATION_S3_ERROR";
    ClientDetailCode[ClientDetailCode["AGE_VERIFICATION_REKOGNITION_ERROR"] = 40006] = "AGE_VERIFICATION_REKOGNITION_ERROR";
    ClientDetailCode[ClientDetailCode["AGE_VERIFICATION_IMAGE_TOO_OLD"] = 40007] = "AGE_VERIFICATION_IMAGE_TOO_OLD";
    ClientDetailCode[ClientDetailCode["AGE_VERIFICATION_NO_METADATA"] = 40008] = "AGE_VERIFICATION_NO_METADATA";
    ClientDetailCode[ClientDetailCode["AGE_VERIFICATION_INVALID_METADATA"] = 40009] = "AGE_VERIFICATION_INVALID_METADATA";
    // Studio Error Codes
    ClientDetailCode[ClientDetailCode["STUDIO_ERROR_INVALID_STATUS"] = 75001] = "STUDIO_ERROR_INVALID_STATUS";
    // Mirrored from lambdas/studio.ts ClientDetailCode (which is a superset
    // local to that handler). Hoisted here so client packages that import the
    // shared enum can react to throttling responses (HTTP 429) returned by the
    // studio Lambda.
    ClientDetailCode[ClientDetailCode["THROTTLING_ERROR"] = 77002] = "THROTTLING_ERROR";
    // Subscription-constraint rejections from the studio Lambda. Values mirror
    // lambdas/studio.ts ClientDetailCode (the handler-local superset). Daily and
    // weekly limits return HTTP 429; tier gating returns HTTP 403.
    ClientDetailCode[ClientDetailCode["STUDIO_DAILY_JOB_LIMIT_REACHED"] = 75020] = "STUDIO_DAILY_JOB_LIMIT_REACHED";
    ClientDetailCode[ClientDetailCode["STUDIO_WEEKLY_JOB_LIMIT_REACHED"] = 75021] = "STUDIO_WEEKLY_JOB_LIMIT_REACHED";
    ClientDetailCode[ClientDetailCode["STUDIO_JOB_TYPE_NOT_ALLOWED_FOR_TIER"] = 75022] = "STUDIO_JOB_TYPE_NOT_ALLOWED_FOR_TIER";
    // Debug Error Codes
    ClientDetailCode[ClientDetailCode["DEBUG_ACCESS_DENIED"] = 76001] = "DEBUG_ACCESS_DENIED";
    ClientDetailCode[ClientDetailCode["DEBUG_MISSING_FILE_PATH"] = 76002] = "DEBUG_MISSING_FILE_PATH";
    ClientDetailCode[ClientDetailCode["DEBUG_UNKNOWN_OPERATION"] = 76003] = "DEBUG_UNKNOWN_OPERATION";
    ClientDetailCode[ClientDetailCode["DEBUG_MISSING_PARAMETERS"] = 76004] = "DEBUG_MISSING_PARAMETERS";
    // Ops Error Codes
    ClientDetailCode[ClientDetailCode["OPS_ACCESS_DENIED"] = 78001] = "OPS_ACCESS_DENIED";
    ClientDetailCode[ClientDetailCode["OPS_UNKNOWN_OPERATION"] = 78002] = "OPS_UNKNOWN_OPERATION";
    ClientDetailCode[ClientDetailCode["OPS_DASHBOARD_GENERATING"] = 78003] = "OPS_DASHBOARD_GENERATING";
    ClientDetailCode[ClientDetailCode["OPS_DASHBOARD_READY"] = 78004] = "OPS_DASHBOARD_READY";
    ClientDetailCode[ClientDetailCode["UNEXPECTED_ERROR"] = 7001] = "UNEXPECTED_ERROR";
})(ClientDetailCode || (ClientDetailCode = {}));
export const createErrorResponse = (statusCode, errorMessage, clientDetailCode) => {
    const errorBody = {
        error: errorMessage,
        clientDetailCode: clientDetailCode,
    };
    const response = {
        statusCode: statusCode,
        body: JSON.stringify(errorBody),
        headers: CORS_HEADERS,
    };
    return response;
};
/**
 * Inverted-timestamp constants and helpers.
 *
 * Many DynamoDB sort keys across the platform are constructed with a
 * 13-digit "inverted timestamp" prefix (`INVERTED_TIMESTAMP_MAX - Date.now()`)
 * so that lexicographically smaller keys correspond to newer records. This is
 * used by jobIds (`${invertedTs}-${uuid}`), messageIds, conversationIds, and
 * postIds. The constant `9999999999999` is exactly 13 digits, and `Date.now()`
 * remains ≤ 13 digits until the year 2286, so the prefix is always 13 digits
 * for any plausible runtime — making lexicographic comparison reliable.
 */
export const INVERTED_TIMESTAMP_MAX = 9999999999999;
/**
 * Threshold for distinguishing a normal epoch-ms timestamp from an inverted one.
 * Any value above this is treated as inverted (`INVERTED_TIMESTAMP_MAX - epochMs`).
 * Chosen so it sits between "year 2096 epoch ms" and "inverted prefix for
 * present-day timestamps", which are far apart.
 */
export const INVERTED_TIMESTAMP_THRESHOLD = 4000000000000;
/**
 * Build the 13-digit inverted-timestamp prefix used in sort-key IDs (jobIds,
 * messageIds, conversationIds, postIds, etc.) so that lexicographically smaller
 * IDs correspond to newer records.
 */
export const buildInvertedTimestampPrefix = (epochMs = Date.now()) => {
    return INVERTED_TIMESTAMP_MAX - epochMs;
};
/**
 * Extract the original creation time (epoch ms) from an ID that begins with a
 * 13-digit inverted-timestamp prefix. Returns null if the leading characters
 * are not a plausible inverted timestamp (so callers can safely fall through
 * for unrecognized ID formats rather than acting on garbage).
 *
 * Examples:
 *   getCreatedAtFromInvertedId("8234567890123-abc-def") -> 1765432109876
 *   getCreatedAtFromInvertedId("not-an-id")             -> null
 */
export const getCreatedAtFromInvertedId = (id) => {
    if (!id) {
        return null;
    }
    const match = id.match(/^(\d{13})/);
    if (!match || match[1] === undefined) {
        return null;
    }
    const inverted = parseInt(match[1], 10);
    const createdAt = INVERTED_TIMESTAMP_MAX - inverted;
    // Reject anything that decodes to nonsense: non-positive, or absurdly far in
    // the future (allow a small skew for clock drift).
    if (createdAt <= 0 || createdAt > Date.now() + 24 * 60 * 60 * 1000) {
        return null;
    }
    return createdAt;
};
/**
 * Normalize a timestamp field that may have been written as an inverted
 * timestamp by mistake. If `ts` looks like an inverted prefix (above
 * `INVERTED_TIMESTAMP_THRESHOLD`), convert it back to a normal epoch ms;
 * otherwise return it unchanged.
 */
export const normalizePossiblyInvertedTimestamp = (ts) => {
    return ts > INVERTED_TIMESTAMP_THRESHOLD ? INVERTED_TIMESTAMP_MAX - ts : ts;
};
export const STUDIO_MODELS = {
    "Text-to-Image-1": {
        id: "Text-to-Image-1",
        displayName: "Text-to-Image 1",
        jobType: "text-to-image",
        defaultPositivePrompt: "Image is highly detailed award winning cinematic photorealistic.",
        defaultNegativePrompt: "watermark, text, anime, cartoon, reflective surface, blurry, low quality, distorted, ugly, abnormal anatomy",
        technicalName: "HiDreamI1Fast",
        available: true,
        description: "Fast photorealistic image generation",
    },
    "Text-to-Image-2": {
        id: "Text-to-Image-2",
        displayName: "Text-to-Image 2",
        jobType: "text-to-image",
        defaultPositivePrompt: "Stunning detailed photorealistic cinematic image",
        defaultNegativePrompt: "blurry, low quality, distorted, ugly, bad anatomy, deformed, poorly drawn, cartoon, anime",
        technicalName: "OmniGen2",
        available: true,
        description: "Versatile generation model",
    },
    "Text-to-Image-3": {
        id: "Text-to-Image-3",
        displayName: "Text-to-Image 3",
        jobType: "text-to-image",
        defaultPositivePrompt: "Highly detailed, unfiltered, photorealistic, real life image.",
        defaultNegativePrompt: "This low quality greyscale unfinished sketch is inaccurate and flawed. The image is very blurred and lacks detail with excessive chromatic aberrations and artifacts. The image is overly saturated with excessive bloom. It has a toony aesthetic with bold outlines and flat colors. Image shows deformed fingers and other abnormal anatomy.",
        technicalName: "Chroma",
        available: true,
        description: "Color-fused generation",
    },
    "Text-to-Image-4": {
        id: "Text-to-Image-4",
        displayName: "Text-to-Image 4",
        jobType: "text-to-image",
        defaultPositivePrompt: "A clear, well-composed image, photorealistic cinematic image",
        defaultNegativePrompt: "text, watermark",
        technicalName: "SDXLSimple",
        available: true,
        description: "Stable diffusion model",
    },
    "Text-to-Image-5": {
        id: "Text-to-Image-5",
        displayName: "Text-to-Image 5",
        jobType: "text-to-image",
        defaultPositivePrompt: "A highly detailed, photorealistic image with excellent composition and lighting.",
        defaultNegativePrompt: "text, watermark, blurry, anime, cartoon, fake, abnormal",
        technicalName: "QwenImageT2I",
        available: true,
        description: "Advanced text-to-image generation",
    },
    "Text-to-Image-6": {
        id: "Text-to-Image-6",
        displayName: "Text-to-Image 6",
        jobType: "text-to-image",
        defaultPositivePrompt: "Image is highly detailed award winning cinematic photorealistic.",
        defaultNegativePrompt: "watermark, text, anime, cartoon, reflective surface, blurry, low quality, distorted, ugly, abnormal anatomy",
        technicalName: "HiDreamI1Dev",
        available: true,
        description: "Photorealistic image generation",
    },
    "Text-to-Image-7": {
        id: "Text-to-Image-7",
        displayName: "Text-to-Image 7",
        jobType: "text-to-image",
        defaultPositivePrompt: "Highly detailed, hyperrealistic photo.",
        defaultNegativePrompt: "cartoon, anime, fake",
        technicalName: "NSFWApi",
        available: true,
        description: "NSFW-capable image generation",
    },
    "Image-to-Image-1": {
        id: "Image-to-Image-1",
        displayName: "Image-to-Image 1",
        jobType: "image-to-image",
        defaultPositivePrompt: "Do not change anything else.",
        defaultNegativePrompt: "cartoon, anime, fake, inconsistent shadows, inconsistent lighting",
        technicalName: "QwenImageEdit",
        available: true,
        description: "Image editing and modification",
    },
    "Image-to-Image-2": {
        id: "Image-to-Image-2",
        displayName: "Image-to-Image 2",
        jobType: "image-to-image",
        defaultPositivePrompt: "Do not change anything else.",
        defaultNegativePrompt: "fake, deformed, blurry, over saturation, bad anatomy, disfigured, poorly drawn face, mutation, mutated, extra_limb, ugly, poorly drawn hands, fused fingers, messy drawing, broken legs censor, censored, censor_bar",
        technicalName: "OmniGen2ImageEdit",
        available: true,
        description: "Image editing and modification",
    },
    "Image-to-Image-3": {
        id: "Image-to-Image-3",
        displayName: "Image-to-Image 3",
        jobType: "image-to-image",
        defaultPositivePrompt: "Do not change anything else.",
        defaultNegativePrompt: "reflective surface, inconsistent shadows, inconsistent lighting, cartoon, anime, fake",
        technicalName: "QwenImageEdit50Steps2",
        available: true,
        description: "High-quality image editing with 50 steps",
    },
};
export const getStudioModel = (modelId) => {
    return STUDIO_MODELS[modelId];
};
export const getAvailableModelsForJobType = (jobType) => {
    return Object.values(STUDIO_MODELS).filter((model) => model.jobType === jobType && model.available);
};
// Studio Job Status Constants
export const STUDIO_JOB_STATUS = {
    PENDING: "pending",
    PROCESSING: "processing",
    COMPLETED: "completed",
    FAILED: "failed",
};
/**
 * Predict the S3 key of a studio job's first output media file from the
 * job's identifiers and type. Used by the studio worker to upload (it
 * writes to this key), the agents project (so an LLM agent can reference
 * "the image I just made" by stable path in a multi-turn conversation),
 * and any other client that needs a stable handle to a job's output
 * before the worker finishes.
 *
 * The convention matches the worker's actual upload key
 * (`studio-worker/studioWorker.ts`): `u/{userId}/studio/{jobId}/result_001.{ext}`.
 * Sequence number is `001` for the first output — multi-output workflows
 * increment from there but that's a worker-side detail.
 *
 * Extension rules (mirrors the worker's `getFileExtension` logic):
 *   - `text-to-video` / `image-to-video` → `mp4`
 *   - `image-to-image` whose source is `.png` → `png` (matches source)
 *   - everything else (text-to-image, image-to-image with non-png source) → `jpg`
 *
 * Pure function — no I/O, no dependencies. Safe to copy across projects.
 */
export function studioJobOutputPath(args) {
    const ext = studioJobOutputExtension(args.jobType, args.sourceImagePath);
    return `u/${args.userId}/studio/${args.jobId}/result_001.${ext}`;
}
/**
 * Output file extension a studio job will use for its first result file,
 * given the job type and (for image-to-image) the source-image path.
 * Mirrors the worker's branching exactly so predicted paths line up
 * with what actually gets uploaded.
 */
export function studioJobOutputExtension(jobType, sourceImagePath) {
    if (jobType === "text-to-video" || jobType === "image-to-video") {
        return "mp4";
    }
    if (jobType === "image-to-image" &&
        sourceImagePath !== undefined &&
        sourceImagePath.toLowerCase().endsWith(".png")) {
        return "png";
    }
    return "jpg";
}
export const SUBSCRIPTION_CONSTRAINTS = {
    F: {
        maxMediaJobsPerDay: 5,
        maxMediaJobsPerWeek: 10,
        videoGenerationEnabled: false,
        audioGenerationEnabled: false,
    },
    P: {
        maxMediaJobsPerDay: 10,
        maxMediaJobsPerWeek: 25,
        videoGenerationEnabled: false,
        audioGenerationEnabled: true,
    },
    M: {
        maxMediaJobsPerDay: null,
        maxMediaJobsPerWeek: null,
        videoGenerationEnabled: true,
        audioGenerationEnabled: true,
    },
};
export const getSubscriptionConstraints = (subType = "F") => {
    return SUBSCRIPTION_CONSTRAINTS[subType];
};
/**
 * Maximum age of an encrypted bearer session, measured from the bearer's
 * `sessionStart`. Single source of truth for EVERY authorizer of the
 * MediaShare bearer: the infra lambdas (`bearer.ts` checkSessionExpiration)
 * and the agents server (`fastify-auth.ts` bearer freshness) must both use
 * this value, so a bearer accepted at one gate is never rejected at another.
 * (A 6h agent-server window diverging from the 30-day infra window caused
 * prod HTTP 424 chat failures on 2026-07-19.) What a user may do within a
 * session is bounded by SUBSCRIPTION_CONSTRAINTS, not by shorter auth
 * windows.
 */
export const MAX_BEARER_SESSION_AGE_MS = 30 /* days */ * 24 * 60 * 60 * 1000;
// Age Verification Types and Interfaces
export var AgeVerificationStatus;
(function (AgeVerificationStatus) {
    AgeVerificationStatus["NOT_VERIFIED"] = "not_verified";
    AgeVerificationStatus["VERIFIED"] = "verified";
    AgeVerificationStatus["FAILED"] = "failed";
})(AgeVerificationStatus || (AgeVerificationStatus = {}));
// Content Policy Constants
export const CONTENT_POLICY_MODEL_ID = "us.anthropic.claude-haiku-4-5-20251001-v1:0";
const CONTENT_POLICY_RESPONSE_FORMAT = `Respond with ONLY a JSON object:
{"violation": false}
or:
{"violation": true, "reason": "<brief reason that does not expose the rule text>"}`;
function buildContentPolicyPrompt(target, policies, liberal) {
    const targetLabel = target === "prompt" ? "image generation prompt" : "image";
    const preamble = liberal
        ? `You are a liberal content policy evaluator. Determine if the following ${targetLabel} strictly violates the letter of any of these policies. Near violations do not count. Policies are:`
        : `You are a content policy evaluator. Determine if the following ${targetLabel} violates any of these policies:`;
    return `${preamble}\n\n${policies}\n\n${CONTENT_POLICY_RESPONSE_FORMAT}`;
}
function formatPolicies(policies) {
    return policies.map((p, i) => `${i + 1}. ${p}`).join("\n");
}
const FACE_POLICY = "FACE_REPLACEMENT: Replaces a human face with a DIFFERENT human face of a known person such as a celebrity or a politician. EXCEPTION: Replacing human faces with non-human animal faces is OK.";
const UNDERWEAR_POLICY = "UNDERWEAR: Depicts a person in underwear or high-exposure swimwear like a bikini or thong.";
// Age-verified shared policy fragments
const AV_HARD_DRUGS = "HARD DRUGS: Depicts use of heroin, cocaine, or crystal meth.";
const AV_REAL_VIOLENCE = "REAL VIOLENCE: Depicts photorealistic REAL violence ACTIVELY inflicted on REAL humans. Fantasy, science fiction, horror fiction, inanimate statues or paintings, or other clearly fictional representations are NOT policy violations. People posing while holding weapons is NOT violence.";
const AV_EXTREME_NUDITY = "EXTREME NUDITY: Depicts DIRECT exposure of skin of human genitals, specifically an exposed nipple, penis, labia, vagina, or anus hole, without ANY material covering it at all (such as a wet t-shirt, mesh, or sheer clothing). Merely showing a naked butt without showing the anus hole or genitals skin does not violate this policy. An exposed back is NOT nudity. Near-nudity such as showing a midriff, torso, or back does NOT violate policy.";
const AV_CLOTHING_REMOVAL = "CLOTHING REMOVAL: Depicts humans after removing ALL clothing.";
const DEFAULT_SEX = "SEX: Depicts sexual act such as intercourse or fellatio.";
const DEFAULT_CHILDREN = "CHILDREN: Depicts nudity or sexually suggestive content AND includes children under 18 years of age.";
const AGE_VERIFIED_PROMPT_POLICIES = formatPolicies([
    AV_HARD_DRUGS,
    AV_REAL_VIOLENCE,
    AV_EXTREME_NUDITY,
    AV_CLOTHING_REMOVAL,
    FACE_POLICY,
    DEFAULT_SEX,
    DEFAULT_CHILDREN,
]);
const AGE_VERIFIED_IMAGE_POLICIES = formatPolicies([
    AV_HARD_DRUGS,
    AV_REAL_VIOLENCE,
    AV_EXTREME_NUDITY,
    AV_CLOTHING_REMOVAL,
    FACE_POLICY,
    DEFAULT_SEX,
    DEFAULT_CHILDREN,
]);
// Default shared policy fragments
const DEFAULT_DRUGS = "DRUGS: Depicts drug use or drug paraphernalia.";
const DEFAULT_NUDITY = "NUDITY: Depicts nudity or sexual content. Near-nudity such as showing a midriff or torso or athletic wear or yoga wear is NOT sexual content.";
const DEFAULT_VIOLENCE = "VIOLENCE: Depicts violence, gore, or weapons being ACTIVELY used against REAL people. Fantasy, science fiction, inanimate statues or paintings, or other clearly fictional representations are NOT policy violations. People posing while holding weapons is NOT violence.";
const DEFAULT_PROMPT_POLICIES = formatPolicies([
    DEFAULT_DRUGS,
    DEFAULT_VIOLENCE,
    DEFAULT_NUDITY,
    FACE_POLICY,
    UNDERWEAR_POLICY,
    DEFAULT_SEX,
    DEFAULT_CHILDREN,
]);
const DEFAULT_IMAGE_POLICIES = formatPolicies([
    DEFAULT_DRUGS,
    DEFAULT_VIOLENCE,
    DEFAULT_NUDITY,
    FACE_POLICY,
    DEFAULT_SEX,
    DEFAULT_CHILDREN,
]);
export const AGE_VERIFIED_SYSTEM_PROMPT = buildContentPolicyPrompt("prompt", AGE_VERIFIED_PROMPT_POLICIES, true);
export const DEFAULT_SYSTEM_PROMPT = buildContentPolicyPrompt("prompt", DEFAULT_PROMPT_POLICIES, false);
export const AGE_VERIFIED_IMAGE_SYSTEM_PROMPT = buildContentPolicyPrompt("image", AGE_VERIFIED_IMAGE_POLICIES, true);
export const DEFAULT_IMAGE_SYSTEM_PROMPT = buildContentPolicyPrompt("image", DEFAULT_IMAGE_POLICIES, false);
